import userPlanService from "./userPlanService";
import userService from "./userService";
import {
  getUserId,
  normalizeUserPlan,
  toUserPlanApiPayload,
} from "./types/userPlanTypes";

/**
 * Calcula los días restantes hasta el vencimiento de una fecha.
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {number} Días hasta vencimiento (negativo si ya venció)
 */
export const getExpiryDays = (dateStr) => {
  const today = new Date();
  const expiry = new Date(dateStr);
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Obtiene la lista de usuarios desde User Search.
 * @param {Object} [options]
 * @param {boolean} [options.active] - Si es false, trae usuarios dados de baja
 * @returns {Promise<Array>} Lista normalizada de usuarios/planes
 */
const getUsers = async (options = {}) => {
  const searchParams = {
    includePermissions: false,
  };

  if (options.active !== undefined && options.active !== null) {
    searchParams.active = options.active;
  }

  const response = await userPlanService.searchUsers(searchParams);
  return response.items.map((item) => normalizeUserPlan(item));
};

/**
 * Obtiene el plan de un usuario por user_id y lo fusiona con los datos de la lista.
 * @param {Object} user - Usuario seleccionado desde search/listado
 * @returns {Promise<Object>} Usuario con datos de plan actualizados
 */
const getUserWithPlan = async (user) => {
  const userId = getUserId(user) ?? Number(user?.id);
  if (!Number.isInteger(userId) || userId <= 0) {
    throw new Error("userId inválido");
  }

  const { items, raw } = await userPlanService.getUserPlans(userId);
  const planRaw =
    items[0] ??
    (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {});

  return normalizeUserPlan(
    {
      ...planRaw,
      user_id: userId,
      company:
        user.company ||
        planRaw.company_name ||
        planRaw.companyName ||
        planRaw.company ||
        planRaw.user?.company,
      email: user.email ?? planRaw.email ?? planRaw.user?.email,
      active: user.active ?? planRaw.active ?? planRaw.user?.active,
    }
  );
};

/**
 * Actualiza un usuario existente.
 * @param {Object} user - Usuario con los campos actualizados
 * @returns {Promise<Object>} Usuario actualizado
 */
const updateUser = async (user, overrides = {}) => {
  const payload = toUserPlanApiPayload(user, overrides);
  const updated = await userPlanService.updateUserPlan(payload);
  return normalizeUserPlan({ ...user, ...updated });
};

/**
 * Da de baja un usuario por id.
 * @param {number|string} userId - ID del usuario
 * @returns {Promise<void>}
 */
const deactivateUser = async (userId) => {
  await userPlanService.deactivateUser(userId);
};

/**
 * Crea un nuevo usuario (user-api/save).
 * @param {Object} user - Datos del usuario
 * @returns {Promise<Object>} Respuesta del backend
 */
const createUser = async (user) => {
  const payload = {
    name: user.name,
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    // Sin password en el alta de colaboradores: el backend dispara el invite de
    // Supabase y el colaborador define su contraseña desde el mail.
    ...(user.password ? { password: user.password } : {}),
    active: Boolean(user.active),
    is_owner: Boolean(user.is_owner),
    is_seller: Boolean(user.is_seller),
    company: {
      id: Number(user.company?.id),
      name: user.company?.name ?? "",
      email: user.company?.email ?? "",
    },
    permissions: Array.isArray(user.permissions)
      ? user.permissions
          .map((permission) => ({
            id: Number(permission?.id),
          }))
          .filter((permission) => Number.isFinite(permission.id))
      : [],
  };

  const response = await userService.createUser(payload);
  return response?.data ?? {};
};

/**
 * Crea un user plan (POST /user-api/user-plan).
 * @param {Object} payload
 * @returns {Promise<Object>} Resultado normalizado
 */
const createUserPlan = async (payload) => {
  const created = await userPlanService.createUserPlan(payload);
  return normalizeUserPlan(created);
};

/**
 * Guarda el plan del usuario.
 * @param {Object} payload - Payload para PUT /user-api/user-plan
 * @returns {Promise<Object>} Resultado normalizado
 */
const renewUserPlan = async (user, overrides = {}) => {
  const payload = toUserPlanApiPayload(user, overrides);
  const renewed = await userPlanService.updateUserPlan(payload);
  return normalizeUserPlan({ ...user, ...renewed });
};

const usersManagementService = {
  getUsers,
  getUserWithPlan,
  createUser,
  updateUser,
  deactivateUser,
  createUserPlan,
  renewUserPlan,
  getExpiryDays,
};

export default usersManagementService;
